import React, { useState, useEffect, useRef, useMemo, memo } from 'react';
import {
  ChartCanvas,
  Chart,
  CandlestickSeries,
  BarSeries,
  LineSeries,
  BollingerSeries,
  XAxis,
  YAxis,
  CrossHairCursor,
  MouseCoordinateX,
  MouseCoordinateY,
  EdgeIndicator,
  OHLCTooltip,
  BollingerBandTooltip,
  MovingAverageTooltip,
  discontinuousTimeScaleProviderBuilder,
  GenericChartComponent,
  getAxisCanvas,
  mouseBasedZoomAnchor,
  ema,
  sma,
  bollingerBand,
} from 'react-financial-charts';
import { timeFormat } from 'd3-time-format';
import { Layers, Calendar, Settings2 } from 'lucide-react';
import { useOHLCData, timeframeConfig, intervalConfig } from '../charts/useOHLCData';
import { colors, chartMargin, priceFormat } from '../charts/chartTheme';
import MultiSelectDropdown from '../UI/MultiSelectDropdown';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '../UI/dropdown-menu';

const VISIBLE_CANDLES = 80;
const VOLUME_HEIGHT = 50;

const GridLevelLine = ({ yValue, stroke, label }) => (
  <GenericChartComponent
    canvasDraw={(ctx, moreProps) => {
      const { chartConfig: { yScale, width } } = moreProps;
      const y = Math.round(yScale(yValue));
      ctx.save();
      ctx.strokeStyle = stroke;
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
      if (label) {
        ctx.setLineDash([]);
        ctx.fillStyle = stroke;
        ctx.font = '10px sans-serif';
        ctx.fillText(label, 5, y - 4);
      }
      ctx.restore();
    }}
    canvasToDraw={getAxisCanvas}
    drawOn={['pan']}
  />
);

const TradeMarkers = () => (
  <GenericChartComponent
    canvasDraw={(ctx, moreProps) => {
      const {
        chartConfig: { yScale },
        xScale,
        xAccessor,
        plotData,
      } = moreProps;
      ctx.save();
      for (const d of plotData) {
        if (!d.isTrade) continue;
        const x = xScale(xAccessor(d));
        const isBuy = d.tradeSide === 'buy';
        const baseY = isBuy ? yScale(d.low) : yScale(d.high);
        if (!isFinite(x) || !isFinite(baseY)) continue;

        const color = isBuy ? '#22c55e' : '#ef4444';
        ctx.fillStyle = color;
        ctx.beginPath();
        if (isBuy) {
          // Up arrow below the candle
          const y = baseY + 6;
          ctx.moveTo(x, y);
          ctx.lineTo(x - 5, y + 10);
          ctx.lineTo(x + 5, y + 10);
        } else {
          // Down arrow above the candle
          const y = baseY - 6;
          ctx.moveTo(x, y);
          ctx.lineTo(x - 5, y - 10);
          ctx.lineTo(x + 5, y - 10);
        }
        ctx.closePath();
        ctx.fill();
      }
      ctx.restore();
    }}
    canvasToDraw={getAxisCanvas}
    drawOn={['pan']}
  />
);

const OrderMarkers = ({ orders }) => (
  <GenericChartComponent
    canvasDraw={(ctx, moreProps) => {
      const { chartConfig: { yScale, width } } = moreProps;
      ctx.save();
      orders.forEach((order) => {
        const y = Math.round(yScale(order.price));
        const isFilled = order.status === 'filled';
        const isBuy = order.side === 'buy';
        const color = isBuy ? '#22c55e' : '#ef4444';
        const x = width - 14;

        if (isFilled) {
          // Filled: solid circle
          ctx.beginPath();
          ctx.arc(x, y, 5, 0, Math.PI * 2);
          ctx.fillStyle = color;
          ctx.fill();
          // Checkmark
          ctx.strokeStyle = '#000';
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(x - 2, y);
          ctx.lineTo(x - 0.5, y + 2);
          ctx.lineTo(x + 3, y - 2);
          ctx.stroke();
        } else {
          // Pending: hollow diamond
          ctx.strokeStyle = color;
          ctx.lineWidth = 1.5;
          ctx.setLineDash([]);
          ctx.beginPath();
          ctx.moveTo(x, y - 5);
          ctx.lineTo(x + 4, y);
          ctx.lineTo(x, y + 5);
          ctx.lineTo(x - 4, y);
          ctx.closePath();
          ctx.stroke();
          // Dashed line from left to marker
          ctx.lineWidth = 0.5;
          ctx.globalAlpha = 0.3;
          ctx.setLineDash([2, 3]);
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(x - 8, y);
          ctx.stroke();
          ctx.globalAlpha = 1;
        }
      });
      ctx.restore();
    }}
    canvasToDraw={getAxisCanvas}
    drawOn={['pan']}
  />
);

const ToolbarBtn = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-2 py-1.5 sm:py-0.5 text-xs font-medium rounded transition-colors ${
      active
        ? 'bg-primary/20 text-primary border border-primary/30'
        : 'text-muted-foreground hover:text-foreground hover:bg-white/5 border border-transparent'
    }`}
  >
    {children}
  </button>
);

/* ------------------------------------------------------------------ */
/*  ChartInner — memoized child that owns the ResizeObserver.         */
/*  Dimension state lives here so resizes never re-render the parent. */
/* ------------------------------------------------------------------ */
const ChartInner = memo(function ChartInner({
  containerRef,
  data,
  xScale,
  xAccessor,
  displayXAccessor,
  xExtents,
  symbol,
  loading,
  showVolume,
  showEma20,
  showEma50,
  showSma200,
  showBB,
  ema20,
  ema50,
  sma200,
  bb,
  gridLines,
  orderMarkers,
  chartSettings = {},
  maTooltipOptions,
}) {
  const [dims, setDims] = useState({ width: 0, height: 0 });
  const observerRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const measure = () => {
      const rect = node.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        const w = Math.floor(rect.width);
        const h = Math.floor(rect.height);
        setDims((prev) => {
          if (prev.width === w && prev.height === h) return prev;
          return { width: w, height: h };
        });
      }
    };

    measure();

    const observer = new ResizeObserver(() => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(measure);
    });
    observer.observe(node);
    observerRef.current = observer;

    return () => {
      observer.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [containerRef]);

  const h = dims.height || 400;
  const volumeH = showVolume ? VOLUME_HEIGHT : 0;
  // When volume is hidden, reserve extra space at the bottom for XAxis tick labels
  const effectiveBottomMargin = showVolume ? chartMargin.bottom : 24;
  const priceChartHeight = h - volumeH - chartMargin.top - effectiveBottomMargin;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">No price data available for {symbol}</p>
      </div>
    );
  }

  if (dims.width <= 0 || priceChartHeight <= 0) return null;

  return (
    <ChartCanvas
      width={dims.width}
      height={h}
      data={data}
      xScale={xScale}
      xAccessor={xAccessor}
      displayXAccessor={displayXAccessor}
      xExtents={xExtents}
      margin={{ ...chartMargin, bottom: effectiveBottomMargin }}
      seriesName={`${symbol}-trading`}
      ratio={window.devicePixelRatio || 1}
      mouseMoveEvent={true}
      panEvent={chartSettings.pan !== false}
      zoomEvent={chartSettings.zoom !== false}
      clamp={chartSettings.clamp ? 'both' : 'left'}
      zoomAnchor={mouseBasedZoomAnchor}
    >
      {/* Main price chart */}
      <Chart
        id={1}
        yExtents={(d) => {
          const vals = [];
          if (isFinite(d.high)) vals.push(d.high);
          if (isFinite(d.low)) vals.push(d.low);
          if (showBB && d.bb) {
            if (isFinite(d.bb.top)) vals.push(d.bb.top);
            if (isFinite(d.bb.bottom)) vals.push(d.bb.bottom);
          }
          return vals.length ? vals : [0];
        }}
        height={priceChartHeight}
        padding={{ top: 10, bottom: 10 }}
      >
        <XAxis
          strokeStyle={colors.grid}
          tickStrokeStyle={colors.axis}
          tickLabelFill={colors.axis}
          fontSize={11}
        />
        <YAxis
          strokeStyle={colors.grid}
          tickStrokeStyle={colors.axis}
          tickLabelFill={colors.axis}
          tickFormat={priceFormat}
          fontSize={11}
        />

        <CandlestickSeries
          fill={(d) => (d.close >= d.open ? colors.candleUpFill : colors.candleDownFill)}
          wickStroke={(d) => (d.close >= d.open ? colors.candleUp : colors.candleDown)}
          stroke={(d) => (d.close >= d.open ? colors.candleUp : colors.candleDown)}
        />

        {/* Overlays */}
        {showEma20 && <LineSeries yAccessor={d => d.ema20 ?? undefined} strokeStyle="#f59e0b" strokeWidth={1.5} />}
        {showEma50 && <LineSeries yAccessor={d => d.ema50 ?? undefined} strokeStyle="#8b5cf6" strokeWidth={1.5} />}
        {showSma200 && <LineSeries yAccessor={d => d.sma200 ?? undefined} strokeStyle="#ec4899" strokeWidth={1.5} />}
        {showBB && (
          <BollingerSeries
            yAccessor={d => d.bb}
            strokeStyle={{ top: '#64748b', middle: '#64748b', bottom: '#64748b' }}
            fillStyle="rgba(100, 116, 139, 0.05)"
          />
        )}

        <MouseCoordinateX displayFormat={timeFormat('%b %d %H:%M')} />
        <MouseCoordinateY rectWidth={60} displayFormat={priceFormat} />

        <EdgeIndicator
          itemType="last"
          orient="right"
          edgeAt="right"
          yAccessor={(d) => d.close}
          fill={(d) => (d.close >= d.open ? colors.candleUp : colors.candleDown)}
          displayFormat={priceFormat}
        />

        <OHLCTooltip
          origin={(w) => [w - 220, 16]}
          textFill={(d) => (d.close >= d.open ? colors.candleUp : colors.candleDown)}
          labelFill={colors.tooltipLabel}
        />

        {maTooltipOptions.length > 0 && (
          <MovingAverageTooltip
            origin={(w) => [w - 220, 32]}
            options={maTooltipOptions}
          />
        )}

        {showBB && (
          <BollingerBandTooltip
            origin={(w) => [w - 220, 32 + maTooltipOptions.length * 16]}
            yAccessor={d => d.bb}
            options={bb.options()}
          />
        )}

        {gridLines.map((level) => (
          <GridLevelLine key={`grid-${level}`} yValue={level} stroke={colors.gridLevel} label={`$${level.toFixed(2)}`} />
        ))}

        {orderMarkers.length > 0 && <OrderMarkers orders={orderMarkers} />}

        <TradeMarkers />
      </Chart>

      {/* Volume sub-chart */}
      {showVolume && (
        <Chart id={2} yExtents={(d) => isFinite(d.volume) ? d.volume : 0} height={volumeH} origin={(w, h) => [0, h - volumeH]}>
          <BarSeries
            yAccessor={(d) => d.volume}
            fillStyle={(d) => d.close >= d.open ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}
          />
        </Chart>
      )}

      <CrossHairCursor strokeStyle={chartSettings.crosshair === false ? 'transparent' : colors.crosshair} />
    </ChartCanvas>
  );
});

/* ------------------------------------------------------------------ */
/*  TradingChart — parent owns data, overlays, timeframe state.       */
/*  Passes a plain ref to ChartInner so resizes stay contained.       */
/* ------------------------------------------------------------------ */
const OVERLAY_OPTIONS = [
  { value: 'ema20', label: 'EMA 20' },
  { value: 'ema50', label: 'EMA 50' },
  { value: 'sma200', label: 'SMA 200' },
  { value: 'bb', label: 'Bollinger' },
];

/**
 * @param {object}  props.strategy
 * @param {array}   props.trades
 * @param {array}   props.positions
 * @param {boolean} props.isMobile
 * @param {number}  props.refreshInterval
 * @param {object}  [props.streamData] - Live bar from WS: { date, open, high, low, close, volume }
 */
const TradingChart = ({ strategy, trades, positions, isMobile = false, refreshInterval, streamData }) => {
  const [timeframe, setTimeframe] = useState('1M');
  const [interval, setInterval] = useState('5m');
  const [showEma20, setShowEma20] = useState(true);
  const [showEma50, setShowEma50] = useState(false);
  const [showSma200, setShowSma200] = useState(false);
  const [showBB, setShowBB] = useState(false);
  const [showVolume, setShowVolume] = useState(true);
  const [chartSettings, setChartSettings] = useState({
    zoom: true,
    pan: true,
    clamp: true,
    crosshair: true,
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const containerRef = useRef(null);

  // Build overlay selection array for mobile MultiSelectDropdown
  const selectedOverlays = useMemo(() => {
    const sel = [];
    if (showEma20) sel.push('ema20');
    if (showEma50) sel.push('ema50');
    if (showSma200) sel.push('sma200');
    if (showBB) sel.push('bb');
    return sel;
  }, [showEma20, showEma50, showSma200, showBB]);

  const handleOverlayChange = (next) => {
    setShowEma20(next.includes('ema20'));
    setShowEma50(next.includes('ema50'));
    setShowSma200(next.includes('sma200'));
    setShowBB(next.includes('bb'));
  };

  // Force volume off on mobile
  const effectiveShowVolume = isMobile ? false : showVolume;

  const symbol = strategy?.parameters?.symbol || 'AAPL';
  const { data: rawData, loading, currentPrice, setData, setCurrentPrice } = useOHLCData(symbol, interval, timeframe, { refreshInterval });

  // Merge live bar data from parent via streamData prop
  // streamData shape: { date: Date, open, high, low, close, volume }
  useEffect(() => {
    if (!streamData || !streamData.date || !rawData.length) return;

    const intMinutes = (intervalConfig[interval]?.minutes || 5);
    const bucketMs = intMinutes * 60 * 1000;
    const bucketTime = Math.floor(streamData.date.getTime() / bucketMs) * bucketMs;
    const last = rawData[rawData.length - 1];
    const lastBucket = Math.floor(last.date.getTime() / bucketMs) * bucketMs;

    if (bucketTime === lastBucket) {
      // Same candle — update in place
      setData((prev) => {
        const updated = [...prev];
        const tip = updated[updated.length - 1];
        updated[updated.length - 1] = {
          ...tip,
          high: Math.max(tip.high, streamData.high),
          low: Math.min(tip.low, streamData.low),
          close: streamData.close,
          volume: tip.volume + (streamData.volume || 0),
        };
        return updated;
      });
    } else if (bucketTime > lastBucket) {
      // New candle — append
      setData((prev) => [...prev, {
        date: new Date(bucketTime),
        open: streamData.open,
        high: streamData.high,
        low: streamData.low,
        close: streamData.close,
        volume: streamData.volume || 0,
      }]);
    }

    setCurrentPrice(streamData.close);
  }, [streamData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Indicator calculators
  const ema20 = useMemo(() => ema().id('ema20').options({ windowSize: 20 }).merge((d, c) => { d.ema20 = c; }).accessor(d => d.ema20), []);
  const ema50 = useMemo(() => ema().id('ema50').options({ windowSize: 50 }).merge((d, c) => { d.ema50 = c; }).accessor(d => d.ema50), []);
  const sma200 = useMemo(() => sma().id('sma200').options({ windowSize: 200 }).merge((d, c) => { d.sma200 = c; }).accessor(d => d.sma200), []);
  const bb = useMemo(() => bollingerBand().merge((d, c) => { d.bb = c; }).accessor(d => d.bb), []);

  const dataWithTrades = useMemo(() => {
    if (!rawData.length) return [];

    let merged = rawData.map((d) => ({ ...d }));

    // Calculate indicators
    merged = ema20(ema50(sma200(bb(merged))));

    if (trades && trades.length > 0) {
      trades.forEach((trade) => {
        const tradeTime = new Date(trade.executed_at || trade.created_at).getTime();
        let closestIdx = -1;
        let closestDist = Infinity;
        merged.forEach((bar, i) => {
          const dist = Math.abs(bar.date.getTime() - tradeTime);
          if (dist < closestDist) { closestDist = dist; closestIdx = i; }
        });
        if (closestIdx >= 0) {
          const existing = merged[closestIdx];
          existing.isTrade = true;
          existing.tradeSide = trade.side;
          existing.tradePrice = parseFloat(trade.price);
          existing.tradeQuantity = trade.quantity;
        }
      });
    }

    return merged;
  }, [rawData, trades, ema20, ema50, sma200, bb]);

  const gridLines = useMemo(() => {
    if (strategy?.type !== 'grid') return [];
    const { lowerBound, upperBound, gridLevels } = strategy.parameters || {};
    if (!lowerBound || !upperBound || !gridLevels) return [];
    const step = (upperBound - lowerBound) / (gridLevels - 1);
    const lines = [];
    for (let i = 0; i < gridLevels; i++) lines.push(parseFloat((lowerBound + step * i).toFixed(2)));
    return lines;
  }, [strategy]);

  // Generate order markers from strategy params
  const orderMarkers = useMemo(() => {
    if (strategy?.type !== 'grid') return [];
    const { lowerBound, upperBound, gridLevels, orderSize = 10 } = strategy.parameters || {};
    if (!lowerBound || !upperBound || !gridLevels) return [];

    const step = (upperBound - lowerBound) / (gridLevels - 1);
    const midPrice = (lowerBound + upperBound) / 2;
    const orders = [];
    for (let i = 0; i < gridLevels; i++) {
      const price = parseFloat((lowerBound + step * i).toFixed(2));
      const isFilled = trades.some(t => Math.abs(parseFloat(t.price) - price) < step * 0.3);
      orders.push({
        price,
        side: price < midPrice ? 'buy' : 'sell',
        status: isFilled ? 'filled' : 'pending',
        quantity: orderSize,
      });
    }
    return orders;
  }, [strategy, trades]);

  const ScaleProvider = useMemo(
    () => discontinuousTimeScaleProviderBuilder().inputDateAccessor((d) => d.date),
    [],
  );

  const { data, xScale, xAccessor, displayXAccessor } = useMemo(() => {
    if (!dataWithTrades.length) return { data: [], xScale: null, xAccessor: null, displayXAccessor: null };
    return ScaleProvider(dataWithTrades);
  }, [dataWithTrades, ScaleProvider]);

  const xExtents = useMemo(() => {
    if (!data.length) return [0, 0];
    const start = xAccessor(data[Math.max(0, data.length - VISIBLE_CANDLES)]);
    const end = xAccessor(data[data.length - 1]) + 3;
    return [start, end];
  }, [data, xAccessor]); // eslint-disable-line react-hooks/exhaustive-deps

  // Moving average tooltip options
  const maTooltipOptions = useMemo(() => {
    const opts = [];
    if (showEma20) opts.push({ yAccessor: ema20.accessor(), type: 'EMA', stroke: '#f59e0b', windowSize: 20 });
    if (showEma50) opts.push({ yAccessor: ema50.accessor(), type: 'EMA', stroke: '#8b5cf6', windowSize: 50 });
    if (showSma200) opts.push({ yAccessor: sma200.accessor(), type: 'SMA', stroke: '#ec4899', windowSize: 200 });
    return opts;
  }, [showEma20, showEma50, showSma200, ema20, ema50, sma200]);

  return (
    <div className="flex flex-col h-full w-full">
      {/* Toolbar — desktop only (mobile overlays moved to bottom bar) */}
      {!isMobile && (
        <div className="flex items-center gap-1.5 px-1 pb-1.5 flex-shrink-0 flex-wrap">
          <span className="text-[10px] text-muted-foreground/50 uppercase tracking-wider mr-1 hidden sm:inline">Overlays</span>
          <ToolbarBtn active={showEma20} onClick={() => setShowEma20(!showEma20)}>EMA 20</ToolbarBtn>
          <ToolbarBtn active={showEma50} onClick={() => setShowEma50(!showEma50)}>EMA 50</ToolbarBtn>
          <ToolbarBtn active={showSma200} onClick={() => setShowSma200(!showSma200)}>SMA 200</ToolbarBtn>
          <ToolbarBtn active={showBB} onClick={() => setShowBB(!showBB)}>Bollinger</ToolbarBtn>
          <div className="w-px h-4 bg-border/30 mx-1 hidden sm:block" />
          <ToolbarBtn active={showVolume} onClick={() => setShowVolume(!showVolume)}>Volume</ToolbarBtn>

          {/* Chart settings gear */}
          <div className="ml-auto relative">
            <button
              type="button"
              onClick={() => setSettingsOpen(!settingsOpen)}
              className={`p-1.5 rounded transition-colors ${
                settingsOpen ? 'bg-primary/20 text-primary' : 'text-muted-foreground/50 hover:text-foreground hover:bg-white/5'
              }`}
              title="Chart Settings"
            >
              <Settings2 className="h-3.5 w-3.5" />
            </button>
            {settingsOpen && (
              <div className="absolute right-0 top-full mt-1 z-50 bg-card border border-border rounded-lg shadow-xl p-3 min-w-[160px]">
                <p className="text-[10px] text-muted-foreground/50 uppercase tracking-wider mb-2">Chart Behavior</p>
                {[
                  { key: 'zoom', label: 'Zoom' },
                  { key: 'pan', label: 'Pan' },
                  { key: 'clamp', label: 'Clamp Edges' },
                  { key: 'crosshair', label: 'Crosshair' },
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center justify-between py-1.5 cursor-pointer group">
                    <span className="text-xs text-foreground group-hover:text-primary transition-colors">{label}</span>
                    <button
                      type="button"
                      onClick={() => setChartSettings((s) => ({ ...s, [key]: !s[key] }))}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        chartSettings[key] ? 'bg-primary' : 'bg-muted-foreground/30'
                      }`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                        chartSettings[key] ? 'translate-x-[18px]' : 'translate-x-[3px]'
                      }`} />
                    </button>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Chart area — ref passed to ChartInner which owns the ResizeObserver */}
      <div
        ref={containerRef}
        className="flex-1 min-h-0 w-full select-none"
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
        style={{ touchAction: 'none', WebkitUserDrag: 'none' }}
      >
        <ChartInner
          containerRef={containerRef}
          data={data}
          xScale={xScale}
          xAccessor={xAccessor}
          displayXAccessor={displayXAccessor}
          xExtents={xExtents}
          symbol={symbol}
          loading={loading}
          showVolume={effectiveShowVolume}
          showEma20={showEma20}
          showEma50={showEma50}
          showSma200={showSma200}
          showBB={showBB}
          ema20={ema20}
          ema50={ema50}
          sma200={sma200}
          bb={bb}
          gridLines={gridLines}
          orderMarkers={orderMarkers}
          maTooltipOptions={maTooltipOptions}
          chartSettings={chartSettings}
        />
      </div>

      {/* Bottom bar: interval + timeframe selectors */}
      {isMobile ? (
        <div className="flex items-center justify-between pt-2 flex-shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border bg-card text-muted-foreground hover:text-foreground hover:border-foreground/20 transition-colors"
              >
                <Calendar className="h-3.5 w-3.5" />
                {intervalConfig[interval]?.label || interval} / {timeframe}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[180px]">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground/60">Interval</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={interval} onValueChange={setInterval}>
                {Object.entries(intervalConfig).map(([key, cfg]) => (
                  <DropdownMenuRadioItem key={key} value={key}>{cfg.label}</DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground/60">Timeframe</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={timeframe} onValueChange={setTimeframe}>
                {Object.keys(timeframeConfig).map((tf) => (
                  <DropdownMenuRadioItem key={tf} value={tf}>{tf}</DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          <MultiSelectDropdown
            label="Overlays"
            icon={Layers}
            options={OVERLAY_OPTIONS}
            selected={selectedOverlays}
            onChange={handleOverlayChange}
          />
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row justify-between items-center pt-2 flex-shrink-0 gap-2">
          <div className="flex items-center gap-2">
            <select
              value={interval}
              onChange={(e) => setInterval(e.target.value)}
              className="px-2.5 py-1 text-xs bg-card border border-border text-foreground rounded focus:outline-none focus:ring-1 focus-visible:ring-ring"
            >
              {Object.entries(intervalConfig).map(([key, cfg]) => (
                <option key={key} value={key}>{cfg.label}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-1 flex-wrap">
            {Object.keys(timeframeConfig).map((tf) => (
              <button
                type="button"
                key={tf}
                onClick={() => setTimeframe(tf)}
                className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                  timeframe === tf
                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    : 'bg-card text-muted-foreground hover:text-foreground hover:bg-white/5 border border-border'
                }`}
              >
                {tf}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TradingChart;
